import { t } from "@lingui/core/macro";
import { type ChangeEvent, forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import "./CustomColormapsManager.scss";

type ImportStatus = {
  kind: "success" | "error";
  message: string;
};

type ImportColormapResponse = {
  id?: string | null;
};

export type CustomColormapsUploaderHandle = {
  openFilePicker: () => void;
  clearStatus: () => void;
};

type CustomColormapsUploaderProps = {
  onImportColormap: (props: { fileName: string; file: File }) => Promise<ImportColormapResponse | undefined>;
  onImportSuccess: (uploadedColormapId: string) => void;
};

export const CustomColormapsUploader = forwardRef<CustomColormapsUploaderHandle, CustomColormapsUploaderProps>(
  ({ onImportColormap, onImportSuccess }, ref) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [importStatus, setImportStatus] = useState<ImportStatus | null>(null);

    const clearStatus = useCallback(() => {
      setImportStatus(null);
    }, []);

    const openFilePicker = useCallback(() => {
      clearStatus();
      fileInputRef.current?.click();
    }, [clearStatus]);

    useImperativeHandle(
      ref,
      () => ({
        openFilePicker,
        clearStatus,
      }),
      [clearStatus, openFilePicker],
    );

    const handleImportFileChange = useCallback(
      async (event: ChangeEvent<HTMLInputElement>) => {
        const input = event.currentTarget;
        const file = input.files?.[0];
        if (!file) {
          return;
        }

        const baseName = file.name.replace(/\.[^/.]+$/, "") || file.name;
        setImportStatus(null);

        try {
          const uploadedColormap = await onImportColormap({ fileName: baseName, file });
          if (!uploadedColormap?.id) {
            throw new Error("Upload response missing colormap id");
          }

          onImportSuccess(uploadedColormap.id);
          setImportStatus({
            kind: "success",
            message: t`Colormap imported successfully.`,
          });
        } catch (error) {
          console.error("Failed to import colormap:", error);
          setImportStatus({
            kind: "error",
            message: t`Failed to import colormap. Allowed file types: json, csv`,
          });
        } finally {
          input.value = "";
        }
      },
      [onImportColormap, onImportSuccess],
    );

    return (
      <>
        <input
          ref={fileInputRef}
          hidden
          type="file"
          accept=".json,.csv"
          data-testid="custom-colormap-import-input"
          onClick={(event) => {
            event.currentTarget.value = "";
          }}
          onChange={handleImportFileChange}
        />
        {importStatus && (
          <div
            className={`importStatus importStatus--${importStatus.kind}`}
            role={importStatus.kind === "error" ? "alert" : "status"}
          >
            {importStatus.message}
          </div>
        )}
      </>
    );
  },
);

CustomColormapsUploader.displayName = "CustomColormapsUploader";
